/**
 * <xp-scroller> — horizontal pill row with arrow buttons.
 *
 * Markup contract:
 *   [data-scroller-track]  the overflow-x scroll container
 *   [data-scroller-prev]   button that scrolls back about one view
 *   [data-scroller-next]   button that scrolls forward about one view
 * Sets data-can-prev / data-can-next on the host so CSS shows the matching arrow
 * and edge fade only when there is more to see in that direction. Mouse users can
 * also turn the wheel to move the row sideways.
 */
class XpScroller extends HTMLElement {
  #abort = null;
  #observer = null;

  connectedCallback() {
    const track = this.querySelector('[data-scroller-track]');
    if (!track) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const rtl = getComputedStyle(this).direction === 'rtl';

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const pos = Math.abs(track.scrollLeft);
      this.toggleAttribute('data-can-prev', pos > 4);
      this.toggleAttribute('data-can-next', max - pos > 4);
    };

    const step = (direction) => {
      const amount = Math.max(track.clientWidth * 0.8, 200) * direction * (rtl ? -1 : 1);
      track.scrollBy({ left: amount, behavior: 'smooth' });
    };

    this.querySelector('[data-scroller-prev]')?.addEventListener('click', () => step(-1), { signal });
    this.querySelector('[data-scroller-next]')?.addEventListener('click', () => step(1), { signal });
    track.addEventListener('scroll', update, { passive: true, signal });

    // A vertical wheel over the row scrolls it sideways while it still can move.
    track.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 0) return;
      const pos = Math.abs(track.scrollLeft);
      if ((event.deltaY < 0 && pos <= 0) || (event.deltaY > 0 && pos >= max - 1)) return;
      event.preventDefault();
      track.scrollLeft += rtl ? -event.deltaY : event.deltaY;
    }, { passive: false, signal });

    this.#observer = new ResizeObserver(update);
    this.#observer.observe(track);

    // Bring the current collection's pill into view.
    const current = track.querySelector('[aria-current="page"]');
    if (current) current.scrollIntoView({ block: 'nearest', inline: 'center' });
    update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#abort = null;
    this.#observer = null;
  }
}

if (!customElements.get('xp-scroller')) customElements.define('xp-scroller', XpScroller);
